import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MapPin, X } from "lucide-react";
import { toast } from "sonner";

// One-time-per-login nudge: if a signed-in member hasn't shared their location yet, ask once.
// Sharing drops an anonymous blip on the Talent Map. Denial never blocks anything.
const SESSION_KEY = "sc_loc_prompted";

export function LocationPrompt() {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !["salesman", "business"].includes(user.role)) return;
    if (user.location_set) return;
    let seen = false;
    try { seen = sessionStorage.getItem(SESSION_KEY) === "1"; } catch { seen = false; }
    if (seen) return;
    const t = setTimeout(() => setOpen(true), 1200); // let the app settle first
    return () => clearTimeout(t);
  }, [user]);

  const markSeen = () => { try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* ignore */ } };
  const dismiss = () => { markSeen(); setOpen(false); };

  const share = () => {
    if (!navigator.geolocation) { toast.error("Location isn't available on this device."); dismiss(); return; }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await api.post("/account/location", { lat: pos.coords.latitude, lon: pos.coords.longitude });
          await refresh();
          toast.success("You're on the Talent Map!");
        } catch { toast.error("Couldn't save your location."); }
        finally { setBusy(false); markSeen(); setOpen(false); }
      },
      () => { setBusy(false); markSeen(); setOpen(false); toast.message("No problem, you can share it later in Settings."); },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 }
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent className="bg-slate-900 border-white/10 text-white max-w-sm text-center" data-testid="location-prompt">
        <button onClick={dismiss} data-testid="location-prompt-close" className="absolute right-3 top-3 text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
        <div className="mx-auto w-14 h-14 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-1">
          <MapPin className="w-7 h-7 text-emerald-400" />
        </div>
        <h2 className="text-xl font-black tracking-tight">Show up on the Talent Map</h2>
        <p className="text-sm text-slate-400 leading-relaxed">
          Share your general location to add your anonymous blip to the live map. It&rsquo;s just a dot, no name, address, or details are ever shown.
        </p>
        <div className="flex flex-col gap-2 mt-2">
          <Button data-testid="location-prompt-allow" onClick={share} disabled={busy}
            className="rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold h-11">
            {busy ? "Sharing…" : "Share my location"}
          </Button>
          <button data-testid="location-prompt-skip" onClick={dismiss} className="text-sm text-slate-400 hover:text-white">Maybe later</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default LocationPrompt;
